import { NgOptimizedImage } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  Signal,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { finalize, switchMap } from 'rxjs';
import { AuthService } from '../../../core/services/auth/auth.service';
import { AlertaService } from '../../../shared/utils/alerta.service';
import {
  BreadcrumbComponent,
  BreadcrumbItem,
} from '../../../shared/components/breadcrumb/breadcrumb.component';
import { LucideModule } from '../../../shared/modules/lucide.module';
import { EtapaAnexosComponent } from './etapa-anexos/etapa-anexos.component';
import { EtapaConfirmacaoComponent } from './etapa-confirmacao/etapa-confirmacao.component';
import { EtapaInformacoesComponent } from './etapa-informacoes/etapa-informacoes.component';
import { EtapaSolicitacaoComponent } from './etapa-solicitacao/etapa-solicitacao.component';
import { EtapasServicoComponent } from './etapas-servico/etapas-servico.component';
import { AcessosServicoService } from '../services/acessos-servico.service';
import { CatalogoPublicoService } from '../services/catalogo-publico.service';
import { ServicoDetalhePublicoService } from '../services/servico-detalhe-publico.service';
import { CategoriaServicosService } from '../services/categoria-servicos.service';
import { ScrollNavegacaoService } from '../../../core/services/scroll-navegacao.service';
import {
  ETAPAS_SOLICITACAO,
  ErroSolicitacaoServico,
  EtapaSolicitacaoId,
} from '../models/solicitacao-servico.model';
import {
  SolicitacaoServicoPublicaService,
  mapearErroSolicitacao,
} from '../services/solicitacao-servico-publica.service';
import { SolicitacaoServicoStore } from './solicitacao-servico.store';

interface ServicoDetalhe {
  titulo: string;
  descricao: string;
  banner?: string;
  /**
   * O código do serviço no adm, quando a rota é a de um serviço PUBLICADO. Fica indefinido
   * nas rotas dos catálogos estáticos, que não têm origem no banco — e é essa distinção que
   * decide se a carta é buscada na API ou se a página abre pelo caminho antigo.
   */
  codServicoSrv?: number;
}

const BANNERS = 'assets/images/banners';

const TRILHA: readonly BreadcrumbItem[] = [{ titulo: 'Serviços', link: '/servicos' }];


/** Serviços com conteúdo próprio; exportado para o guard saber quais rotas existem. */
export const CATALOGO_SERVICOS: Record<string, ServicoDetalhe> = {
  'assistencia-social': {
    titulo: 'Assistência Social',
    descricao:
      'O Programa de Assistência Social da Prefeitura de Salvador atende famílias em situação de vulnerabilidade social, garantindo acesso a benefícios, serviços e programas de proteção social básica e especial, conforme previsto na Política Nacional de Assistência Social (PNAS).',
    banner: `${BANNERS}/banner-defaul.png`,
  },
  animais: {
    titulo: 'Animais',
    descricao: 'Animais domésticos e animais silvestres.',
    banner: `${BANNERS}/banner-defaul2png.png`,
  },
  documentos: {
    titulo: 'Documentos',
    descricao: 'Dívida Ativa, processos administrativos, certidões e serviços para servidor.',
  },
  educacao: {
    titulo: 'Educação',
    descricao: 'Material e Uniforme Escolar, CEUS, Matrícula e TEG.',
  },
};

@Component({
  selector: 'app-pages-servicos',
  templateUrl: './pages-servicos.component.html',
  styleUrls: ['./pages-servicos.component.css'],
  imports: [
    NgOptimizedImage,
    BreadcrumbComponent,
    LucideModule,
    EtapasServicoComponent,
    EtapaInformacoesComponent,
    EtapaSolicitacaoComponent,
    EtapaAnexosComponent,
    EtapaConfirmacaoComponent,
  ],
  // O store do fluxo nasce e morre com a página: cada etapa é destruída pelo @switch, e os dados
  // (inclusive os arquivos) precisam sobreviver às idas e vindas.
  providers: [SolicitacaoServicoStore],
})
export class PagesServicosComponent {
  private readonly categoriaServicosService = inject(CategoriaServicosService);
  private readonly catalogoPublico = inject(CatalogoPublicoService);
  private readonly servicoDetalhe = inject(ServicoDetalhePublicoService);
  private readonly solicitacaoPublica = inject(SolicitacaoServicoPublicaService);
  protected readonly store = inject(SolicitacaoServicoStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly alerta = inject(AlertaService);
  private readonly scrollNavegacao = inject(ScrollNavegacaoService);
  private readonly injector = inject(Injector);
  private readonly acessosServico = inject(AcessosServicoService);
  private readonly auth = inject(AuthService);

  private readonly usuario = toSignal(this.auth.currentUser, {
    initialValue: this.auth.currentUserValue,
  });

  /**
   * O último serviço cujo acesso esta instância da página registrou. Um campo, não um signal:
   * só impede que a mesma abertura conte duas vezes quando o `effect` roda de novo.
   */
  private acessoRegistrado: number | null = null;

  /** O mesmo papel de `acessoRegistrado`, para a contagem pública, que não depende de sessão. */
  private acessoPublicoRegistrado: number | null = null;

  readonly rotaPagina = input('');

  /** Os rótulos, para o stepper compartilhado — que trabalha por posição. */
  readonly etapas = ETAPAS_SOLICITACAO.map((etapa) => etapa.rotulo);

  /** A etapa por identidade; a posição é derivada só para o stepper e a navegação. */
  readonly etapaAtualId = signal<EtapaSolicitacaoId>('informacoes');

  readonly etapaAtual = computed(() => ETAPAS_SOLICITACAO.findIndex((etapa) => etapa.id === this.etapaAtualId()));

  /**
   * A posição mais avançada liberada. A solicitação só é liberada após login ou escolha
   * anônima, e volta a zero quando o fluxo é reiniciado.
   */
  readonly etapaLiberada = signal(0);

  /** Um envio no ar: o botão de conclusão fica bloqueado contra o duplo clique. */
  readonly enviando = signal(false);

  /** Elemento da trilha de etapas: é por ele que a etapa recém-aberta começa na tela. */
  private readonly trilhaEtapas: Signal<ElementRef<HTMLElement> | undefined> = viewChild(
    'trilhaEtapas',
    { read: ElementRef }
  );

  /** Login feito pelo card da etapa de informações — sobrevive à troca de etapas. */
  readonly sessaoIniciada = signal(false);

  /** Contador de pedidos de foco no card de acesso; cada incremento leva o cursor até ele. */
  readonly focarLogin = signal(0);

  readonly servico = computed<ServicoDetalhe>(() => {
    const rota = this.rotaPagina();
    const detalhe = CATALOGO_SERVICOS[rota];
    const categoria = this.categoriaServicosService
      .categoriaServicos()
      .find((item) => item.rotaPagina === rota);

    // O serviço vindo da API, quando a rota é o slug do nome dele. Tem precedência sobre
    // os catálogos estáticos: é dado real, cadastrado no adm, e não conteúdo de exemplo.
    const publicado = this.catalogoPublico
      .categorias()
      .flatMap((item) => item.servicos)
      .find((item) => item.link === `/${rota}`);

    return {
      titulo:
        publicado?.titulo ?? categoria?.titulo ?? detalhe?.titulo ?? this.formatarRotaPagina(rota),
      descricao:
        publicado?.descricao ??
        detalhe?.descricao ??
        categoria?.descricao ??
        'Conteúdo deste serviço em breve.',
      banner: detalhe?.banner,
      // O id do serviço publicado é o que liga a rota (um slug) à API (que busca por
      // código). O catálogo já o trouxe na listagem; guardá-lo aqui evita uma segunda
      // regra de slug no back-end.
      codServicoSrv: publicado ? Number(publicado.id) : undefined,
    };
  });

  /**
   * A carta do serviço, buscada por código assim que o catálogo resolve o slug.
   *
   * O `effect` é o que liga as duas chamadas: enquanto a listagem não chegou, o código é
   * indefinido e o service não dispara request nenhuma. Nas rotas dos catálogos estáticos
   * ele permanece indefinido para sempre, e a página abre sem carta — como antes.
   */
  readonly carta = this.servicoDetalhe.carta;

  readonly cartaCarregando = this.servicoDetalhe.carregando;

  readonly cartaFalhou = this.servicoDetalhe.falhou;

  readonly trilha = TRILHA;

  readonly podeAvancar = computed(() => {
    switch (this.etapaAtualId()) {
      case 'solicitacao':
        return this.store.validoSolicitacao();
      case 'anexos':
        return this.store.validoAnexos();
      default:
        return true;
    }
  });

  /** Com o protocolo na tela, o fluxo terminou: a navegação some e as etapas ficam travadas. */
  readonly concluida = computed(() => this.store.respostaAbertura() !== null);

  constructor() {
    // A carta é buscada assim que o catálogo resolve o slug da rota num código. Enquanto
    // a listagem não chegou — ou numa rota estática, que nunca terá código — o valor é
    // `null` e o service não dispara chamada nenhuma.
    effect(() => this.servicoDetalhe.definirServico(this.servico().codServicoSrv ?? null));

    // Cada abertura da página por quem está logado conta um acesso — o "Mais acessados" dos
    // favoritos. Visitante e rota estática (sem código) não contam; trocar de serviço na
    // mesma instância conta o novo.
    effect(() => {
      const codigo = this.servico().codServicoSrv;

      if (!codigo || !this.usuario()?.token || codigo === this.acessoRegistrado) return;

      this.acessoRegistrado = codigo;
      this.acessosServico.registrar(codigo);
    });

    // Cada abertura conta também na contagem pública — o ranking de "Serviços mais procurados"
    // da homepage —, com ou sem sessão. É outro contador: o do cidadão acima não muda.
    effect(() => {
      const codigo = this.servico().codServicoSrv;

      if (!codigo || codigo === this.acessoPublicoRegistrado) return;

      this.acessoPublicoRegistrado = codigo;
      this.acessosServico.registrarPublico(codigo);
    });

    let primeiraEtapa = true;

    // Trocar de etapa é uma ação de navegação: a nova etapa abre a partir do seu topo,
    // sem rolagem animada. A abertura da página fica de fora — ali o topo é o da rota.
    effect(() => {
      this.etapaAtual();

      if (primeiraEtapa) {
        primeiraEtapa = false;
        return;
      }

      afterNextRender(() => this.rolarParaEtapa(), { injector: this.injector });
    });
  }

  /** "Tentar novamente" do aviso de falha da carta. */
  recarregarCarta(): void {
    this.servicoDetalhe.recarregar();
  }

  private rolarParaEtapa(): void {
    this.scrollNavegacao.aoInicioDaEtapa(this.trilhaEtapas()?.nativeElement);
  }

  irParaEtapa(indice: number): void {
    if (indice > this.etapaLiberada() || this.concluida()) return;

    this.irPara(ETAPAS_SOLICITACAO[indice].id);
  }

  avancar(): void {
    if (!this.podeAvancar()) {
      this.store.destacarPendencias();
      return;
    }

    // Um aviso de envio anterior (vaga, formulário) já foi visto e tratado ao sair da etapa.
    this.store.erroEnvio.set(null);

    const proxima = Math.min(this.etapaAtual() + 1, ETAPAS_SOLICITACAO.length - 1);

    this.etapaLiberada.update((liberada) => Math.max(liberada, proxima));
    this.irPara(ETAPAS_SOLICITACAO[proxima].id);
  }

  voltar(): void {
    this.irPara(ETAPAS_SOLICITACAO[Math.max(this.etapaAtual() - 1, 0)].id);
  }

  /** Entrada pelo login do sistema: a solicitação segue IDENTIFICADA, pelo token da sessão. */
  solicitar(): void {
    this.store.entrarIdentificado();
    this.abrirSolicitacao();
  }

  /**
   * "Solicitar anonimamente" — e o gov.br, que ainda chama o mesmo caminho (sem integração): a
   * solicitação segue ANÔNIMA, sem cadastro para exibir.
   */
  seguirSemIdentificacao(): void {
    this.store.entrarAnonimo();
    this.abrirSolicitacao();
  }

  private abrirSolicitacao(): void {
    // O formulário do serviço só é buscado aqui, quando o cidadão decide solicitar — e não
    // junto da carta, que todo visitante lê.
    this.solicitacaoPublica.definirServico(this.servico().codServicoSrv ?? null);

    // O desafio de formulário nasce com a etapa: envelhece enquanto o cidadão preenche e chega
    // ao envio com a idade mínima que a API exige.
    this.store.desafio.carregar();

    this.etapaLiberada.update((liberada) => Math.max(liberada, 1));
    this.irPara('solicitacao');
  }

  private irPara(etapa: EtapaSolicitacaoId): void {
    this.etapaAtualId.set(etapa);
  }

  autenticar(): void {
    this.sessaoIniciada.set(true);
    this.solicitar();
  }

  /** O "Solicitar" do topo leva ao card de acesso: a etapa só é liberada pela escolha feita lá. */
  irParaLogin(): void {
    this.irPara('informacoes');
    this.focarLogin.update((vezes) => vezes + 1);
  }

  encerrarSessao(): void {
    this.sessaoIniciada.set(false);
    this.store.limpar();
    this.etapaLiberada.set(0);
    this.irPara('informacoes');
  }

  async cancelarSolicitacao(): Promise<void> {
    const confirmado = await this.alerta.confirmar({
      titulo: 'Cancelar solicitação?',
      texto: 'Os dados preenchidos até aqui serão descartados.',
      confirmar: 'Sim, cancelar',
      cancelar: 'Continuar preenchendo',
    });

    if (confirmado) {
      this.reiniciar();
    }
  }

  /**
   * Envia a solicitação, anônima ou identificada — o modo vai no `FormData`, e na identificada
   * o solicitante é quem o token da sessão diz.
   */
  async concluir(): Promise<void> {
    if (this.enviando() || this.concluida()) return;

    if (!this.store.podeConcluir()) {
      this.store.destacarPendencias();
      return;
    }

    const codServico = this.store.formulario()?.codServicoSrv;

    if (!codServico) return;

    const confirmado = await this.alerta.confirmar({
      titulo: 'Enviar solicitação?',
      texto: 'Depois de enviada, a solicitação não pode ser alterada pelo portal.',
      confirmar: 'Sim, enviar',
      cancelar: 'Revisar',
    });

    if (!confirmado) return;

    this.enviando.set(true);
    this.store.erroEnvio.set(null);

    const corpo = this.store.paraFormData(codServico);

    // Se a carga do desafio falhou, `paraEnvio` pede outro agora; falhando de novo, cai no erro
    // de rede de sempre.
    this.store.desafio
      .paraEnvio()
      .pipe(
        switchMap((desafio) => this.solicitacaoPublica.abrir(corpo, desafio)),
        finalize(() => this.enviando.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (resposta) => {
          this.store.respostaAbertura.set(resposta);
          this.etapaLiberada.set(ETAPAS_SOLICITACAO.length - 1);
          this.irPara('confirmacao');
        },
        error: (erro: unknown) => this.tratarErroDeEnvio(mapearErroSolicitacao(erro)),
      });
  }

  /**
   * Cada erro leva o cidadão aonde ele pode resolver, com os dados preservados. A mensagem fica
   * no store, e a etapa de destino a exibe.
   */
  private tratarErroDeEnvio(erro: ErroSolicitacaoServico): void {
    this.store.erroEnvio.set(erro);

    switch (erro.tipo) {
      case 'vaga':
        // O horário foi tomado enquanto o cidadão preenchia: escolher outro.
        this.store.agendamento.set(null);
        this.store.destacarPendencias();
        this.irPara('solicitacao');
        break;

      case 'formulario':
        // O órgão regravou o serviço no adm: as respostas antigas apontam para campos que não
        // existem mais. O formulário é recarregado e os campos específicos, refeitos.
        this.store.descartarRespostasCampos();
        this.store.recarregarFormulario();
        this.irPara('solicitacao');
        break;

      case 'desafio':
        // O desafio foi recusado (vencido, reusado): pede outro, e a Confirmação mostra a
        // mensagem com o botão de envio habilitado — tudo o que foi preenchido fica.
        this.store.desafio.renovar();
        break;

      case 'sessao':
        // O token venceu e o interceptor já encerrou a sessão. Os dados ficam: o cidadão entra de
        // novo pela etapa Informações e volta aqui com tudo preenchido.
        this.sessaoIniciada.set(false);
        break;

      default:
        // Validação, identificação exigida, limite e indisponibilidade: a Confirmação mostra.
        break;
    }
  }

  private reiniciar(): void {
    this.irPara('informacoes');
    this.etapaLiberada.set(0);
    this.sessaoIniciada.set(false);
    this.store.limpar();
  }

  /** "Nova solicitação" do recibo. */
  novaSolicitacao(): void {
    this.reiniciar();
  }

  private formatarRotaPagina(rotaPagina: string): string {
    return rotaPagina
      .split('-')
      .filter(Boolean)
      .map((parte) => parte.charAt(0).toUpperCase() + parte.slice(1))
      .join(' ');
  }
}
